import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, ArrowRight, Sparkles, Check, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';

// Matrice prix : gamme × format
const PRICING = {
  iconiques: { decouverte: { format: '30 ml', price: 17, label: 'Iconiques' }, signature: { format: '50 ml', price: 25, label: 'Iconiques' } },
  pepites: { decouverte: { format: '10 ml', price: 15, label: 'Pépites' }, signature: { format: '50 ml', price: 39, label: 'Pépites' } },
  audacieux: { decouverte: { format: '10 ml', price: 25, label: 'Audacieux' }, signature: { format: '50 ml', price: 59, label: 'Audacieux' } },
};

const GAMME_DESC = {
  iconiques: 'Élégance intemporelle. Une fragrance qui traverse les saisons.',
  pepites: 'Originalité rare. Un parfum confidentiel, qui ne ressemble qu\'à vous.',
  audacieux: 'Caractère expressif. Une signature qui ne passe pas inaperçue.',
};

const GAMME_COLORS = {
  iconiques: '#5C2018',
  pepites: '#1E7A6A',
  audacieux: '#2E120D',
};

const DiagnosticModal = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({ gamme: null, format: null, perso: null });
  const [addCoffret, setAddCoffret] = useState(false);
  const { add: addToCart } = useCart();
  const navigate = useNavigate();

  if (!open) return null;

  const reset = () => {
    setStep(1);
    setAnswers({ gamme: null, format: null, perso: null });
    setAddCoffret(false);
  };

  const handleAnswer = (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step < 3) setStep(step + 1);
    else setStep(4);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const result = step === 4 && answers.gamme && answers.format
    ? PRICING[answers.gamme][answers.format]
    : null;

  const totalPrice = result ? result.price + (answers.perso === 'yes' ? 5 : 0) + (addCoffret ? 5 : 0) : 0;
  const gammeColor = result ? GAMME_COLORS[answers.gamme] : '#5C2018';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#332724]/60 backdrop-blur-sm" data-testid="diagnostic-modal" onClick={handleClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFAF4] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
      >
        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white border border-gold flex items-center justify-center hover:bg-[#5C2018] hover:text-[#FDFAF4] transition-colors z-10"
          data-testid="diagnostic-close"
        >
          <X size={18} />
        </button>

        {/* Progress dots */}
        {step <= 3 && (
          <div className="flex justify-center gap-2 pt-8 pb-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full transition-all ${s === step ? 'bg-[#5C2018] w-8' : s < step ? 'bg-[#B8922A]' : 'bg-[#B8922A]/25'}`}
              />
            ))}
          </div>
        )}

        <div className="p-8 md:p-10 pt-2">
          {/* Header */}
          {step <= 3 && (
            <div className="text-center mb-6">
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2 flex items-center justify-center gap-2">
                <Sparkles size={14} /> Diagnostic olfactif · 30 secondes
              </p>
              <h3 className="text-2xl md:text-3xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Trouvez votre parfum signature
              </h3>
            </div>
          )}

          {/* Question 1 — Gamme */}
          {step === 1 && (
            <div data-testid="diagnostic-q1">
              <p className="text-base text-[#332724] font-medium mb-5 text-center">Question 1 / 3 — Votre style olfactif ?</p>
              <div className="space-y-3">
                {[
                  { id: 'iconiques', emoji: '🌹', title: 'Élégant et intemporel', desc: 'Une fragrance classique, qui traverse les modes.' },
                  { id: 'pepites', emoji: '💎', title: 'Original et confidentiel', desc: 'Un parfum rare, qui ne s\'achète pas partout.' },
                  { id: 'audacieux', emoji: '🖤', title: 'Audacieux et expressif', desc: 'Une signature qui marque, qui se remarque.' },
                ].map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleAnswer('gamme', c.id)}
                    className="w-full text-left bg-white hover:bg-[#5C2018] hover:text-[#FDFAF4] border border-gold p-4 rounded-xl transition-all group"
                    data-testid={`diagnostic-gamme-${c.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-2xl">{c.emoji}</span>
                      <div className="flex-1">
                        <p className="font-medium text-[#5C2018] group-hover:text-[#FDFAF4] mb-1">{c.title}</p>
                        <p className="text-xs text-[#332724]/70 group-hover:text-[#FDFAF4]/85">{c.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Question 2 — Format */}
          {step === 2 && (
            <div data-testid="diagnostic-q2">
              <p className="text-base text-[#332724] font-medium mb-5 text-center">Question 2 / 3 — Pour quelle occasion ?</p>
              <div className="space-y-3">
                {[
                  { id: 'decouverte', emoji: '✨', title: 'Une rencontre, un essai', desc: 'Je veux découvrir une fragrance avant de m\'engager. Petit format.' },
                  { id: 'signature', emoji: '💄', title: 'Mon parfum au quotidien', desc: 'Ma signature à porter tous les jours. Format 50 ml.' },
                ].map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleAnswer('format', c.id)}
                    className="w-full text-left bg-white hover:bg-[#5C2018] hover:text-[#FDFAF4] border border-gold p-4 rounded-xl transition-all group"
                    data-testid={`diagnostic-format-${c.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-2xl">{c.emoji}</span>
                      <div className="flex-1">
                        <p className="font-medium text-[#5C2018] group-hover:text-[#FDFAF4] mb-1">{c.title}</p>
                        <p className="text-xs text-[#332724]/70 group-hover:text-[#FDFAF4]/85">{c.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Question 3 — Personnalisation */}
          {step === 3 && (
            <div data-testid="diagnostic-q3">
              <p className="text-base text-[#332724] font-medium mb-5 text-center">Question 3 / 3 — Une touche unique ?</p>
              <div className="space-y-3">
                {[
                  { id: 'yes', emoji: '✨', title: 'Oui, une création personnalisée', desc: 'Personnalisation +5€ — formule unique, enregistrée à vie au fichier client.' },
                  { id: 'no', emoji: '🌹', title: 'Non, la fragrance telle quelle', desc: 'Je veux une signature pure, sans modification.' },
                ].map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleAnswer('perso', c.id)}
                    className="w-full text-left bg-white hover:bg-[#5C2018] hover:text-[#FDFAF4] border border-gold p-4 rounded-xl transition-all group"
                    data-testid={`diagnostic-perso-${c.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-2xl">{c.emoji}</span>
                      <div className="flex-1">
                        <p className="font-medium text-[#5C2018] group-hover:text-[#FDFAF4] mb-1">{c.title}</p>
                        <p className="text-xs text-[#332724]/70 group-hover:text-[#FDFAF4]/85">{c.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Résultat */}
          {step === 4 && result && (
            <div className="text-center" data-testid="diagnostic-result">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#B8922A]/15 flex items-center justify-center">
                <Check size={28} className="text-[#B8922A]" />
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">Votre Rituel idéal</p>
              <h3 className="text-3xl md:text-4xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', color: gammeColor }}>
                Rituel {answers.format === 'decouverte' ? 'Découverte' : 'Signature'}
              </h3>
              <p className="text-xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: gammeColor }}>
                Les {result.label} · {result.format}
              </p>

              <div className="bg-white rounded-xl border border-gold p-5 mb-5 inline-block">
                <p className="text-3xl font-light" style={{ color: gammeColor, fontFamily: "'Cormorant Garamond', serif" }}>
                  {totalPrice}€
                </p>
                {(answers.perso === 'yes' || addCoffret) && (
                  <p className="text-xs text-[#1E7A6A] italic mt-1">
                    {result.price}€
                    {answers.perso === 'yes' && ' + 5€ personnalisation'}
                    {addCoffret && ' + 5€ coffret cadeau'}
                  </p>
                )}
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#332724]/70 mt-2">
                  + consultation 15 min offerte
                </p>
              </div>

              <p className="text-sm text-[#332724] font-light italic mb-4 max-w-md mx-auto">
                {GAMME_DESC[answers.gamme]}
              </p>

              {/* Toggle Coffret cadeau (option offrir) */}
              <label
                className={`flex items-start gap-3 max-w-lg mx-auto mb-4 p-4 rounded-xl border-2 cursor-pointer transition-all text-left ${
                  addCoffret
                    ? 'bg-[#B8922A]/10 border-[#B8922A] shadow-[0_4px_15px_rgba(184,146,42,0.2)]'
                    : 'bg-white border-[#B8922A]/30 hover:border-[#B8922A]/60'
                }`}
                data-testid="diagnostic-coffret-toggle"
              >
                <input
                  type="checkbox"
                  checked={addCoffret}
                  onChange={(e) => setAddCoffret(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#B8922A]"
                />
                <div className="flex-1">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-[#5C2018]">
                      🎁 C&apos;est un cadeau ? Ajouter un coffret
                    </p>
                    <span className="text-base font-light text-[#B8922A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                      +5€
                    </span>
                  </div>
                  <p className="text-[11px] text-[#332724]/75 font-light leading-relaxed">
                    Écrin bordeaux/doré, papier de soie, ruban satiné, étiquette manuscrite au prénom du destinataire.
                  </p>
                  {addCoffret && (
                    <p className="text-[10px] text-[#1E7A6A] italic mt-2 leading-snug">
                      ℹ️ Coffret cadeau ajouté à votre Rituel — le tout réglé en une fois au checkout.
                    </p>
                  )}
                </div>
              </label>

              {/* Rappel du concept Harmony */}
              <div className="bg-[#1E7A6A]/8 border border-[#1E7A6A]/30 rounded-xl px-5 py-4 mb-6 max-w-lg mx-auto text-left">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#1E7A6A] font-medium mb-2">
                  💡 Comment ça marche ?
                </p>
                <p className="text-xs text-[#332724] font-light leading-relaxed mb-3">
                  Vous réservez votre <strong>format</strong> et votre <strong>gamme</strong>.
                  Le <strong>parfum exact</strong> vous sera révélé lors de votre
                  <strong> consultation 15 min offerte</strong> (en boutique ou en visio) :
                  nos experts identifieront votre signature olfactive et choisiront
                  <em> la fragrance qui vous correspond vraiment</em> parmi les
                  {answers.gamme === 'iconiques' ? ' Iconiques' : answers.gamme === 'pepites' ? ' Pépites' : ' Audacieux'}.
                </p>
                <Link
                  to="/boutique/rituels"
                  onClick={handleClose}
                  className="inline-flex items-center gap-1.5 text-xs text-[#5C2018] hover:text-[#B8922A] underline underline-offset-2 font-medium transition-colors"
                  data-testid="diagnostic-concept-link"
                >
                  En savoir plus sur les Rituels
                  <ArrowRight size={12} />
                </Link>
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    const formatKey = answers.format === 'decouverte' ? 'dec' : 'sig';
                    const gammeShort = answers.gamme === 'iconiques' ? 'ico' : answers.gamme === 'pepites' ? 'pep' : 'aud';
                    const formatMl = result.format.replace(' ', '').toLowerCase();
                    addToCart({
                      sku: `rit-${formatKey}-${gammeShort}-${formatMl}`,
                      label: `Harmony · Rituel ${answers.format === 'decouverte' ? 'Découverte' : 'Signature'} · ${result.label} ${result.format}`,
                      unit_price: result.price,
                    });
                    if (answers.perso === 'yes') {
                      addToCart({ sku: 'option-perso', label: 'Harmony · Personnalisation Rituel (formule unique)', unit_price: 5 });
                    }
                    if (addCoffret) {
                      addToCart({ sku: 'option-coffret', label: 'Harmony · Coffret cadeau (supplément écrin)', unit_price: 5 });
                    }
                    toast.success(`Rituel ${result.label} ajouté au panier · ${totalPrice}€`);
                    handleClose();
                    navigate('/panier');
                  }}
                  className="btn-primary px-7 py-3 rounded-full text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2"
                  data-testid="diagnostic-cta-add-cart"
                >
                  <ShoppingBag size={14} /> Ajouter au panier · {totalPrice}€
                </button>
              </div>
              <button
                onClick={reset}
                className="mt-4 text-xs uppercase tracking-[0.2em] text-[#332724]/60 hover:text-[#5C2018] underline"
                data-testid="diagnostic-restart"
              >
                Refaire le test
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticModal;
